import React, {
  useState,
  useEffect,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TextField,
  useMediaQuery,
  Collapse,
  Skeleton,
} from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CustomPagination from "../../components/PotatoProtection/LargeScreen/Landing/CustomPagination";
import useThemeRender from "../../theme/useThemeRender";
import { darkTheme, lightTheme } from "../../theme/Theme";
import { tableCellStyle, headerTextStyle, dividerStyle } from "./tableStyle";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import NoRecordFounds from "../NoRecordsFound";

// Sortable Row Component
const SortableRow = ({
  id,
  row,
  columns,
  isDarkTheme,
  onRowClick,
  renderActions,
  cellRenderer,
  expandedRow,
  renderChildComponent,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e) => {
    if (!isDragging) onRowClick(row);
  };

  return (
    <Fragment key={row.id}>
      <TableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        onClick={handleClick}
        sx={{
          cursor: "pointer",
          boxShadow: "0px 2px 0px 0px var(--color-border-default) inset",
          borderBottom: "2px solid var(--color-border-default)",
          "&:hover": {
            backgroundColor: isDarkTheme
              ? "rgba(255, 255, 255, 0.1)"
              : "#f5f5f5",
          },
          "&:nth-of-type(odd)": {
            color: isDarkTheme
              ? darkTheme.textColorLayer4
              : lightTheme.textColorLayer4,
          },
        }}
      >
        <TableCell
          sx={{
            border: "none",
            width: "40px",
            padding: "0 8px",
            cursor: "grab",
          }}
        >
          {/* DRAG HANDLE ICON */}
          <DragIndicatorIcon {...listeners} />
        </TableCell>

        {columns.map((col) => (
          <TableCell
            key={col.id}
            sx={{
              border: "none",
              color: isDarkTheme
                ? darkTheme.textColorLayer4
                : lightTheme.textColorLayer4,
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "14px",
            }}
          >
            {col.cellStyle === "cellStyle"
              ? cellRenderer
                ? cellRenderer(row[col.id], row)
                : row[col.id]
              : col.id.includes("actions") && renderActions
              ? renderActions(row, col)
              : row[col.id]}
          </TableCell>
        ))}
      </TableRow>

      {renderChildComponent && (
        <TableRow>
          <TableCell colSpan={columns.length + 1} sx={{ p: 0, border: "none" }}>
            <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
              <Box>{renderChildComponent(row)}</Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

// Regular Table Row (non-draggable)
const TableRowContent = ({
  row,
  columns,
  isDarkTheme,
  onRowClick,
  renderActions,
  cellRenderer,
  expandedRow,
  renderChildComponent,
}) => (
  <Fragment key={row.id}>
    <TableRow
      onClick={() => onRowClick(row)}
      sx={{
        cursor: "pointer",
        boxShadow: "0px 2px 0px 0px var(--color-border-default) inset",
        borderBottom: "2px solid var(--color-border-default)",
        "&:hover": {
          backgroundColor: isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
        },
        "&:nth-of-type(odd)": {
          color: isDarkTheme
            ? darkTheme.textColorLayer4
            : lightTheme.textColorLayer4,
        },
      }}
    >
      {columns.map((col) => (
        <TableCell
          key={col.id}
          sx={{
            border: "none",
            color: isDarkTheme
              ? darkTheme.textColorLayer4
              : lightTheme.textColorLayer4,
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "14px",
          }}
        >
          {col.cellStyle === "cellStyle"
            ? cellRenderer
              ? cellRenderer(row[col.id], row)
              : row[col.id]
            : col.id.includes("actions") && renderActions
            ? renderActions(row, col)
            : row[col.id]}
        </TableCell>
      ))}
    </TableRow>

    {renderChildComponent && (
      <TableRow>
        <TableCell colSpan={columns.length} sx={{ p: 0, border: "none" }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <Box>{renderChildComponent(row)}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
    )}
  </Fragment>
);

// Column Header
const ColumnHeader = ({ columns, isDarkTheme, enableDragAndDrop }) => (
  <TableRow>
    {enableDragAndDrop && (
      <TableCell
        sx={{
          width: "40px",
          border: "none",
          backgroundColor: "transparent",
        }}
      />
    )}
    {columns.map((col) => (
      <TableCell
        key={col.id}
        sx={{
          width: col.width || "auto",
          color: isDarkTheme
            ? darkTheme.textColorLayer4
            : lightTheme.textColorLayer4,
          ...tableCellStyle,
        }}
      >
        <Typography
          sx={{
            ...headerTextStyle,
            color: isDarkTheme
              ? darkTheme.textColorLayer4
              : lightTheme.textColorLayer4,
            width: col.headerWidth || "65%",
          }}
        >
          {col.label}
        </Typography>
      </TableCell>
    ))}
  </TableRow>
);

// Filter Row
const ColumnFilters = ({
  columns,
  columnSearch,
  onChange,
  isDarkTheme,
  enableDragAndDrop,
}) => (
  <TableRow>
    {enableDragAndDrop && <TableCell sx={{ padding: 0 }} />}
    {columns.map((col) => (
      <TableCell key={col.id} sx={{ padding: 0 }}>
        {col.filterable && (
          <>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={columnSearch[col.id]}
              onChange={(e) => onChange(col.id, e.target.value)}
              placeholder="Filter"
              sx={{
                width: "70%",
                mb: 2,
                "& .MuiInputBase-root": {
                  height: "24px",
                  border: isDarkTheme ? "1px solid white" : "",
                },
                "& .MuiOutlinedInput-input": {
                  px: 1,
                  py: 0.5,
                  color: isDarkTheme ? "white" : "black",
                },
              }}
            />
            <FilterListIcon
              sx={{
                ml: 1,
                width: 16,
                height: 16,
                fill: isDarkTheme ? "white" : "black",
              }}
            />
          </>
        )}
      </TableCell>
    ))}
  </TableRow>
);

// No Records Row
const NoRecordsRow = ({ columns, isDarkTheme, enableDragAndDrop }) => (
  <TableRow>
    <TableCell
      colSpan={columns.length + (enableDragAndDrop ? 1 : 0)}
      sx={{
        textAlign: "center",
        py: 2,
        fontWeight: "bold",
        color: isDarkTheme ? "white" : "black",
        width: "100%",
      }}
    >
      <NoRecordFounds />
    </TableCell>
  </TableRow>
);

// Main Table Component
const DynamicTable = ({
  columns,
  rows: initialRows,
  searchText,
  onRowClick,
  onRowOrderChange,
  rowsPerPage = 10,
  showColumnFilters = true,
  renderActions,
  expandedRow,
  renderChildComponent,
  cellRenderer,
  enableDragAndDrop = false,
  pagination = true,
  loading = false,
}) => {
  const isDarkTheme = useSelector((state) => state.theme);
  const [page, setPage] = useState(0);
  const isMobile = useMediaQuery("(max-width:800px)");
  const [columnSearch, setColumnSearch] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.id]: "" }), {})
  );
  const [rows, setRows] = useState(initialRows);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement to start dragging
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    setRows(initialRows);

    // ✅ Always flip off initial load after first render,
    // regardless of whether data came back empty
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [initialRows, isInitialLoad]);
  useEffect(() => {
    if (!loading && !dataLoaded) {
      // Mark that our first load is finished
      setDataLoaded(true);
    }
  }, [loading, dataLoaded]);

  useEffect(() => setPage(0), [searchText, columnSearch]);

  const handleColumnSearchChange = useCallback((colId, value) => {
    setColumnSearch((prev) => ({ ...prev, [colId]: value }));
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over.id) {
        setRows((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const newItems = arrayMove(items, oldIndex, newIndex);
          if (onRowOrderChange) onRowOrderChange(newItems);
          return newItems;
        });
      }
    },
    [onRowOrderChange]
  );

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    return rows.filter((row) => {
      const matchesGlobal = searchText
        ? Object.values(row).some((val) =>
            String(val ?? "")
              .toLowerCase()
              .includes(searchText.toLowerCase())
          )
        : true;

      const matchesColumn = columns.every((col) => {
        if (!col.filterable) return true;
        const filterValue = columnSearch[col.id];
        if (!filterValue) return true;
        const cellValue = String(row[col.id] ?? "").toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
      setPage(0);
      return matchesGlobal && matchesColumn;
    });
  }, [rows, searchText, columnSearch, columns]);
  const [noRecords, setNoRecords] = useState(false);

  useEffect(() => {
    if (dataLoaded && !loading) {
      setNoRecords(filteredRows.length === 0);
    }
  }, [filteredRows, dataLoaded, loading]);

  const paginatedRows = useMemo(() => {
    if (!pagination) return filteredRows;
    return filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredRows, page, rowsPerPage, pagination]);

  return (
    <TableContainer>
      <Table
        sx={{
          borderCollapse: "collapse",
          backgroundColor: useThemeRender("cardBackgroundColorLayer1"),
        }}
      >
        <TableHead>
          <ColumnHeader
            columns={columns}
            isDarkTheme={isDarkTheme}
            enableDragAndDrop={enableDragAndDrop}
          />
          {showColumnFilters && !isMobile && (
            <ColumnFilters
              columns={columns}
              columnSearch={columnSearch}
              onChange={handleColumnSearchChange}
              isDarkTheme={isDarkTheme}
              enableDragAndDrop={enableDragAndDrop}
            />
          )}
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ p: 0, border: "none" }}>
              <Box sx={dividerStyle} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={index}>
                {enableDragAndDrop && (
                  <TableCell sx={{ border: "none" }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.id} sx={{ border: "none" }}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : enableDragAndDrop ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={paginatedRows.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
              >
                {paginatedRows.length > 0
                  ? paginatedRows.map((row) => (
                      <SortableRow
                        key={row.id}
                        id={row.id}
                        row={row}
                        columns={columns}
                        isDarkTheme={isDarkTheme}
                        onRowClick={onRowClick}
                        renderActions={renderActions}
                        cellRenderer={cellRenderer}
                        expandedRow={expandedRow}
                        renderChildComponent={renderChildComponent}
                      />
                    ))
                  : noRecords && (
                      <NoRecordsRow
                        columns={columns}
                        isDarkTheme={isDarkTheme}
                        enableDragAndDrop={enableDragAndDrop}
                      />
                    )}
              </SortableContext>
            </DndContext>
          ) : (
            <>
              {paginatedRows.length > 0
                ? paginatedRows.map((row) => (
                    <TableRowContent
                      key={row.id}
                      row={row}
                      columns={columns}
                      isDarkTheme={isDarkTheme}
                      onRowClick={onRowClick}
                      renderActions={renderActions}
                      cellRenderer={cellRenderer}
                      expandedRow={expandedRow}
                      renderChildComponent={renderChildComponent}
                    />
                  ))
                : noRecords && (
                    <NoRecordsRow
                      columns={columns}
                      isDarkTheme={isDarkTheme}
                      enableDragAndDrop={enableDragAndDrop}
                    />
                  )}
            </>
          )}
        </TableBody>
      </Table>
      {pagination && filteredRows.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 2 }}>
          <CustomPagination
            page={page}
            setPage={setPage}
            totalPages={Math.ceil(filteredRows.length / rowsPerPage)}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default DynamicTable;
